import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db, auth, storage, appId } from '../firebase-config';
import { collection, query, onSnapshot, doc, updateDoc, addDoc, serverTimestamp, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import toast from 'react-hot-toast';
import { PlusIcon, PaperClipIcon, TrashIcon, XMarkIcon } from './Icons';
import TaskItem from './TaskItem';
import TaskModal from './TaskModal';
import ConfirmationModal from './ConfirmationModal';
import InlineEdit from './InlineEdit';
import StatusSelector from './StatusSelector'; // Importar StatusSelector

const ProcessoDetalheModal = ({ processoId, isOpen, onClose }) => {
  const userId = auth.currentUser?.uid;
  const collectionPath = `artifacts/${appId}/users/${userId}/processos/${processoId}/tasks`;

  const [processo, setProcesso] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!processoId || !isOpen) {
      setLoading(false);
      return;
    }

    setLoading(true);
    
    // Listener para o processo
    const processoRef = doc(db, `artifacts/${appId}/users/${userId}/processos`, processoId);
    const unsubscribeProcesso = onSnapshot(processoRef, (docSnap) => {
      if (docSnap.exists()) {
        setProcesso({ id: docSnap.id, ...docSnap.data() });
      } else {
        setProcesso(null);
      }
    });

    // Listener para as tarefas
    const tasksQuery = query(collection(db, collectionPath));
    const unsubscribeTasks = onSnapshot(tasksQuery, (snapshot) => {
      const tasksData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTasks(tasksData);
    });

    // Listener para os anexos
    const attachmentsPath = `artifacts/${appId}/users/${userId}/processos/${processoId}/attachments`;
    const loadAttachments = async () => {
      try {
        const attachmentsRef = ref(storage, attachmentsPath);
        const filesList = await listAll(attachmentsRef);
        const attachmentsData = await Promise.all(
          filesList.items.map(async (item) => {
            const url = await getDownloadURL(item);
            return { id: item.name, name: item.name, url: url, path: item.fullPath };
          })
        );
        setAttachments(attachmentsData);
      } catch (error) {
        console.error('Erro ao carregar anexos:', error);
        setAttachments([]);
      }
    };

    loadAttachments();
    setLoading(false);

    return () => {
      unsubscribeProcesso();
      unsubscribeTasks();
    };
  }, [processoId, userId, isOpen, collectionPath, appId]);

  const handleUpdateField = useCallback(async (field, value) => {
    if (!processoId || !userId) return;
    try {
      const processoRef = doc(db, `artifacts/${appId}/users/${userId}/processos`, processoId);
      await updateDoc(processoRef, {
        [field]: value,
        updatedAt: serverTimestamp()
      });
      toast.success(`${field.charAt(0).toUpperCase() + field.slice(1)} atualizado!`);
    } catch (error) {
      console.error(`Erro ao atualizar ${field}:`, error);
      toast.error(`Erro ao atualizar ${field} do processo`);
    }
  }, [processoId, userId, appId]);

  const handleUpdateTaskTitle = useCallback(async (taskId, newTitle) => {
    if (!taskId || !newTitle.trim() || !userId) return;
    try {
      await updateDoc(doc(db, collectionPath, taskId), {
        title: newTitle.trim(),
        updatedAt: serverTimestamp()
      });
      toast.success('Título da tarefa atualizado!');
    } catch (error) {
      console.error('Erro ao atualizar título da tarefa:', error);
      toast.error('Erro ao atualizar título da tarefa');
    }
  }, [collectionPath, userId]);

  const handleSaveTask = async (taskData) => {
    if (!userId) return;
    try {
      setLoading(true);
      if (editingTask) {
        await updateDoc(doc(db, collectionPath, editingTask.id), { ...taskData, updatedAt: serverTimestamp() });
        toast.success('Tarefa atualizada com sucesso!');
      } else {
        await addDoc(collection(db, collectionPath), { ...taskData, processoId, userId, completed: false, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
        toast.success('Tarefa criada com sucesso!');
      }
      setIsTaskModalOpen(false);
      setEditingTask(null);
    } catch (error) {
      console.error('Erro ao salvar tarefa:', error);
      toast.error('Erro ao salvar tarefa');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setIsTaskModalOpen(true);
  };

  const handleDeleteTask = async (taskId) => {
    if (!userId) return;
    try {
      await updateDoc(doc(db, collectionPath, taskId), { deleted: true, deletedAt: serverTimestamp() });
      toast.success('Tarefa removida com sucesso!');
    } catch (error) {
      console.error('Erro ao remover tarefa:', error);
      toast.error('Erro ao remover tarefa');
    }
  };

  const handleToggleTask = async (taskId, completed) => {
    if (!userId) return;
    try {
      await updateDoc(doc(db, collectionPath, taskId), { completed, completedAt: completed ? serverTimestamp() : null, updatedAt: serverTimestamp() });
      toast.success(completed ? 'Tarefa concluída!' : 'Tarefa reaberta!');
    } catch (error) {
      console.error('Erro ao atualizar tarefa:', error);
      toast.error('Erro ao atualizar tarefa');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !userId) return;

    setIsUploadingAttachment(true);
    try {
      const attachmentPath = `artifacts/${appId}/users/${userId}/processos/${processoId}/attachments/${file.name}`;
      const attachmentRef = ref(storage, attachmentPath);
      await uploadBytes(attachmentRef, file);
      const downloadURL = await getDownloadURL(attachmentRef);
      const newAttachment = { id: file.name, name: file.name, url: downloadURL, path: attachmentPath };
      setAttachments(prev => [...prev, newAttachment]);
      toast.success('Anexo adicionado com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast.error('Erro ao adicionar anexo');
    } finally {
      setIsUploadingAttachment(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAttachment = async (attachment) => {
    if (!userId) return;
    try {
      const attachmentRef = ref(storage, attachment.path);
      await deleteObject(attachmentRef);
      setAttachments(prev => prev.filter(att => att.id !== attachment.id));
      toast.success('Anexo removido com sucesso!');
    } catch (error) {
      console.error('Erro ao remover anexo:', error);
      toast.error('Erro ao remover anexo');
    }
  };

  const handleOpenConfirmModal = (id) => {
    setItemToDelete(id);
    setIsConfirmModalOpen(true);
  };
  
  const handleStatusChange = async (newStatus) => {
    await handleUpdateField('status', newStatus);
  };

  if (!isOpen) return null;

  if (loading || !processo) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl p-6 w-full max-w-6xl h-5/6 overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4 pb-4 border-b">
          <div className="flex-1">
            <InlineEdit value={processo.title} onSave={(newTitle) => handleUpdateField('title', newTitle)} className="text-2xl font-bold text-gray-800" />
            <InlineEdit value={processo.description} onSave={(newDesc) => handleUpdateField('description', newDesc)} className="text-sm text-gray-600 mt-1" placeholder="Adicionar descrição..." />
          </div>
          <div className="flex items-center gap-4">
             <StatusSelector 
                currentStatus={processo.status}
                onStatusChange={handleStatusChange}
                tasks={tasks.filter(t => !t.deleted)}
             />
             <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2"><XMarkIcon className="h-6 w-6" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
            <div className="space-y-4 overflow-y-auto pr-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">Tarefas ({tasks.filter(t => !t.deleted).length})</h3>
                <button onClick={() => setIsTaskModalOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm">
                  <PlusIcon className="h-4 w-4" /> Nova Tarefa
                </button>
              </div>
              <div className="space-y-3">
                {tasks.filter(task => !task.deleted).map((task) => (
                  <TaskItem key={task.id} task={task} userId={userId} onEdit={handleEditTask} onDelete={handleOpenConfirmModal} onToggle={handleToggleTask} collectionPath={collectionPath} onTitleUpdate={handleUpdateTaskTitle} />
                ))}
                {tasks.filter(task => !task.deleted).length === 0 && (
                  <div className="text-center py-8 text-gray-500"><p>Nenhuma tarefa criada ainda.</p><p className="text-sm mt-2">Clique em "Nova Tarefa" para começar.</p></div>
                )}
              </div>
            </div>

            <div className="space-y-4 overflow-y-auto pr-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">Anexos ({attachments.length})</h3>
                <button onClick={() => fileInputRef.current?.click()} disabled={isUploadingAttachment} className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm">
                  <PaperClipIcon className="h-4 w-4" />{isUploadingAttachment ? 'Enviando...' : 'Adicionar Anexo'}
                </button>
              </div>
              <input ref={fileInputRef} type="file" onChange={handleFileUpload} className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt" />
              <div className="space-y-3">
                {attachments.map((attachment) => (
                  <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3"><PaperClipIcon className="h-5 w-5 text-gray-400" /><a href={attachment.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 font-medium">{attachment.name}</a></div>
                    <button onClick={() => handleDeleteAttachment(attachment)} className="text-red-500 hover:text-red-700 p-1"><TrashIcon className="h-4 w-4" /></button>
                  </div>
                ))}
                {attachments.length === 0 && (
                  <div className="text-center py-8 text-gray-500"><p>Nenhum anexo adicionado ainda.</p><p className="text-sm mt-2">Clique em "Adicionar Anexo" para enviar arquivos.</p></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <TaskModal isOpen={isTaskModalOpen} onClose={() => { setIsTaskModalOpen(false); setEditingTask(null); }} onSave={handleSaveTask} taskToEdit={editingTask} />
      <ConfirmationModal isOpen={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)} onConfirm={() => { if (itemToDelete) handleDeleteTask(itemToDelete); setIsConfirmModalOpen(false); }} title="Confirmar Exclusão" message="Tem certeza que deseja excluir esta tarefa? Esta ação não pode ser desfeita." />
    </div>
  );
};

export default ProcessoDetalheModal;
